import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useWebviewMode } from '../../hooks/useWebviewMode';
import axios from 'axios';
import Swal from 'sweetalert2';
import './style.css';
import { useSelector, useDispatch } from 'react-redux';
import ReactSelect from 'react-select';
import { useUser } from '@clerk/clerk-react';

import { authSelector } from '../../store/slices/auth';
import {
  selectedClientSelector,
  setSelectedClient
} from '../../store/slices/selectedClient';
import { showLoading, hideLoading } from '../../store/slices/loadingState';
import { clientsSelector, getClients } from '../../store/slices/clients';

import textRatingToNumber from '../Client/ReviewsSection/services/textRatingToNumber';

import {
  API_KEY,
  API_REVIEW_UPDATE_MSG_REPLY,
  GET_REVIEWS_PER_CLIENT,
  API_REVIEWS_HANDLER,
  API_REVIEW_APPROVE,
  REVIEWS_GENERATE_REPLY_BY_AI,
  SEO_AUDIT_BASE,
  API_CLIENTS_NAME
} from '../../utils';

import { accountManagers } from '../../utils';

import { Button } from '../../components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../components/ui/table';

import {
  Star,
  Pencil,
  Trash2,
  Check,
  CheckCheck,
  X,
  Filter,
  User,
  Clock,
  RotateCcw,
  Settings,
  MessageSquarePlus,
  Search,
  RefreshCw,
  Loader2
} from 'lucide-react';

import { Checkbox } from '../../components/ui/checkbox';

import { TruncatedText } from '../../components/TruncatedText';
import { getGoogleProfileInfo } from '../../components/GoogleBusinessProfile/services/index';

const Actions = ({ row, refresh, setLoading, onApprove }) => {
  const reviews = useSelector((state) => state.reviews);
  const dispatch = useDispatch();

  const handleCancel = async () => {
    dispatch({
      type: 'reviews/setEditable',
      payload: { id: row.id, editable: false }
    });
  };

  const handleClick = async () => {
    if (reviews?.editable?.id === row.id && reviews?.editable?.editable) {
      dispatch({
        type: 'reviews/setEditable',
        payload: { id: row.id, editable: false }
      });

      if (
        reviews?.editable?.message &&
        reviews?.editable?.message.length > 0 &&
        reviews?.editable?.message !== row.msgReply
      ) {
        dispatch({
          type: 'reviews/save',
          payload: {
            loading: true,
            reviews: reviews?.reviews.map((review) =>
              review.id === row.id
                ? { ...review, msgReply: reviews?.editable?.message }
                : review
            )
          }
        });

        try {
          const response = await axios.post(`${API_REVIEW_UPDATE_MSG_REPLY}/`, {
            id: row.id,
            msgReply: reviews?.editable?.message
          });

          if (response.status === 200) {
            dispatch({ type: 'reviews/save', payload: { loading: false } });
          }
        } catch (err) {
          console.error(err);
        }
      }
    } else if (!reviews?.editable?.editable) {
      dispatch({
        type: 'reviews/setEditable',
        payload: { id: row.id, editable: true, message: row.msgReply }
      });
    }
  };

  const handleApproved = async (value) => {
    try {
      const response = await axios.post(`${API_REVIEW_APPROVE}/`, {
        id: row.reviewId
      });

      if (response.status === 200) {
        onApprove && onApprove(row.id);
        dispatch({
          type: 'reviews/approve',
          payload: reviews?.reviews.map((review) =>
            review.id === row.id ? { ...review, checked: value } : review
          )
        });
      }
    } catch (err) {
      console.error('Error approving review:', err);
    }
  };

  const handleDelete = () => {
    Swal.fire({
      title: 'Delete Review',
      text: 'Are you sure you want to delete?',
      confirmButtonText: 'Yes',
      showCancelButton: true
    }).then((res) => {
      if (res.value) {
        setLoading(true);
        dispatch(showLoading());
        axios
          .post(API_REVIEWS_HANDLER, {
            review: {
              reviewId: row.reviewId,
              reviewer: row.reviewer,
              starRating: row.starRating,
              ...(row.locationId ? { locationId: row.locationId } : {})
            },
            request: { sentBy: 'SUITE', operation: 'deny' }
          })
          .then((response) => {
            dispatch(hideLoading());
            setLoading(false);
            refresh();

            if (response.status === 200) {
              Swal.fire({
                title: 'Success!',
                text: 'Review Deleted ✅',
                showCloseButton: true
              });
            } else {
              Swal.fire({
                title: 'Error!',
                text: 'Something went wrong ❌',
                showCloseButton: true
              });
            }
          });
      }
    });
  };

  return (
    <div className="flex flex-col gap-2">
      {reviews?.editable?.editable && reviews?.editable?.id === row.id ? (
        <>
          <Button
            onClick={handleClick}
            variant="outline"
            size="sm"
            className="h-9 rounded-lg text-sm gap-2 justify-start text-green-600 border-green-200 hover:bg-green-50 hover:text-green-700">
            <Check className="h-4 w-4" />
            Confirm
          </Button>
          <Button
            onClick={handleCancel}
            variant="outline"
            size="sm"
            className="h-9 rounded-lg text-sm gap-2 justify-start text-destructive border-destructive/20 hover:bg-destructive/10 hover:text-destructive">
            <X className="h-4 w-4" />
            Cancel
          </Button>
        </>
      ) : reviews?.loading ? (
        <span className="text-sm text-muted-foreground">Loading...</span>
      ) : row?.checked ? (
        <span className="text-sm text-muted-foreground">Approved</span>
      ) : (
        <>
          <Button
            onClick={handleClick}
            variant="outline"
            size="sm"
            className="h-9 rounded-lg text-sm gap-2 justify-start">
            <Pencil className="h-4 w-4" />
            Edit
          </Button>
          <Button
            onClick={() => handleApproved(true)}
            variant="outline"
            size="sm"
            className="h-9 rounded-lg text-sm gap-2 justify-start hover:bg-green-50 hover:text-green-700"
            style={{
              color: '#16a34a',
              borderColor: '#16a34a !important',
              borderWidth: '1px',
              borderStyle: 'solid'
            }}>
            <Check className="h-4 w-4" />
            Approve
          </Button>
          <Button
            onClick={handleDelete}
            variant="outline"
            size="sm"
            className="h-9 rounded-lg text-sm gap-2 justify-start hover:bg-destructive/10"
            style={{
              color: '#ef4444',
              borderColor: '#ef4444 !important',
              borderWidth: '1px',
              borderStyle: 'solid'
            }}>
            <Trash2 className="h-4 w-4" />
            Delete
          </Button>
        </>
      )}
    </div>
  );
};

const Answer = ({ cell, row }) => {
  const reviews = useSelector((state) => state.reviews);
  const selectedClient = useSelector(selectedClientSelector);
  const editable =
    reviews?.editable?.editable && reviews?.editable?.id === row.id;

  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const handleEditMessage = (msg) => {
    dispatch({ type: 'reviews/editMessage', payload: msg });
  };

  const handleAutoGenerate = async () => {
    try {
      setLoading(true);

      const res = await fetch(REVIEWS_GENERATE_REPLY_BY_AI, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          review: row,
          name: row?.reviewer?.displayName,
          singlePerson: selectedClient?.selectedClient?.singlePerson
        })
      });

      const msg = await res.text();
      if (msg) handleEditMessage(msg);
    } catch (err) {
      console.error('Error generating message', err);
    } finally {
      setLoading(false);
    }
  };

  if (editable) {
    return (
      <div className="relative">
        <textarea
          className="w-full p-2 text-sm border rounded-md min-h-[100px] focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="Edit the review answer message here"
          disabled={loading}
          value={reviews.editable.message || ''}
          onChange={(e) => handleEditMessage(e.target.value)}
        />

        {row?.rev_comment && (
          <button
            type="button"
            onClick={handleAutoGenerate}
            disabled={loading}
            className="absolute bottom-2 right-2 bg-blue-100 hover:bg-blue-200 rounded-full p-2 transition-colors"
            title="Auto Generate">
            <svg
              viewBox="0 0 32 32"
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              style={{
                animation: loading ? 'spin 1s linear infinite' : 'none',
                transform: 'rotate(310deg)'
              }}>
              <path
                d="m16 0c8.836556 0 16 7.163444 16 16s-7.163444 16-16 16-16-7.163444-16-16 7.163444-16 16-16zm7.5013519 8h-5c-.5522847 0-1 .44771525-1 1v5c0 .5522847.4477153 1 1 1 .5522848 0 1-.4477153 1-1l.0011164-2.872584c1.5476338 1.1131888 2.4975317 2.9096298 2.4975317 4.872584 0 3.3137085-2.6862915 6-6 6s-6-2.6862915-6-6c0-2.6791615 1.771854-5.0121287 4.3028795-5.7568853.5298238-.1559011.832948-.71179106.6770468-1.24161488-.1559011-.52982382-.711791-.83294803-1.2416148-.67704688-3.3764809.99353263-5.7383115 4.10331096-5.7383115 7.67554706 0 4.418278 3.581722 8 8 8s8-3.581722 8-8c0-2.3423774-1.015734-4.5083081-2.7082286-6.00072306l2.2095805.00072306c.5522848 0 1-.44771525 1-1s-.4477152-1-1-1z"
                fill="#3766a8"
              />
            </svg>
          </button>
        )}
      </div>
    );
  }

  return (
    <TruncatedText
      text={cell}
      maxLength={60}
      className="text-sm text-muted-foreground"
    />
  );
};

// ─── Development mock data ──────────────────────────────────────────────────

const Reviews = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const { userInfo } = useSelector(authSelector);
  const { selectedClient } = useSelector(selectedClientSelector);
  const { customerList } = useSelector(clientsSelector);
  const { user } = useUser();
  const isWebviewMode = useWebviewMode();
  const userEmail =
    user?.primaryEmailAddress?.emailAddress || userInfo?.email || '';
  const isSuperAdmin =
    userEmail.endsWith('@adlerlocal.com') ||
    userEmail.endsWith('@samxlabs.com') ||
    userEmail.endsWith('@adlersocial.com');

  const [showReplied, setShowReplied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [filter, setFilter] = useState(null);
  const [activePlatform, setActivePlatform] = useState('all'); // 'all', 'google', 'facebook'
  const showGoogleReviews =
    activePlatform === 'google' || activePlatform === 'all';
  const [refresh, setRefresh] = useState(false);
  const [selectedReviews, setSelectedReviews] = useState([]);
  const [isBulkApproving, setIsBulkApproving] = useState(false);

  // Separate states for Google and Facebook reviews
  const [googleReviews, setGoogleReviews] = useState([]);
  const [facebookReviews, setFacebookReviews] = useState([]);
  const [facebookLoading, setFacebookLoading] = useState(false);
  const [ingesting, setIngesting] = useState(false);

  // helpers de fechas
  function getDaysAgoUnix() {
    const today = new Date();
    today.setDate(today.getDate() - 7);
    return Math.floor(today.getTime());
  }
  function getCurrentUnix() {
    return Math.floor(new Date().getTime());
  }
  function formatDateToInput(unixTimestamp) {
    const date = new Date(unixTimestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  const [dateFrom, setDateFrom] = useState(formatDateToInput(getDaysAgoUnix()));
  const [dateTo, setDateTo] = useState(formatDateToInput(getCurrentUnix()));
  const [starsFilter, setStarsFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const [googleAverageRating, setGoogleAverageRating] = useState({
    loading: false,
    data: null
  });

  const toBool = (value) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return (
        normalized === 'true' || normalized === '1' || normalized === 'yes'
      );
    }
    return false;
  };

  const isRejectedReview = (row) => toBool(row?.rejected);
  const isApprovedReview = (row) =>
    toBool(row?.checked) && !isRejectedReview(row);
  const isPendingReview = (row) =>
    !isApprovedReview(row) && !isRejectedReview(row);

  const normalizeReviewRating = (row) => {
    const rawRating = row?.starRating;

    if (typeof rawRating === 'number') return rawRating;

    if (typeof rawRating === 'string') {
      const asNumber = Number(rawRating);
      if (!Number.isNaN(asNumber)) return asNumber;

      try {
        return textRatingToNumber(rawRating.toLowerCase());
      } catch {
        return null;
      }
    }

    return null;
  };

  const getCreatedTimeUnix = (row) => {
    const raw = row?.createdTimeUnix;
    if (typeof raw === 'number') return raw;
    if (typeof raw === 'string') {
      const parsed = Number(raw);
      return Number.isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  };

  const applyFallbackFilters = (rows, queryParams) => {
    if (!queryParams) return rows;

    return rows.filter((item) => {
      if (
        queryParams?.starRating !== undefined &&
        queryParams?.starRating !== null &&
        queryParams?.starRating !== 'all'
      ) {
        const rowRating = normalizeReviewRating(item);
        const targetRating = Number(queryParams.starRating);
        if (rowRating === null || rowRating !== targetRating) {
          return false;
        }
      }

      if (queryParams?.dateFrom) {
        const createdUnix = getCreatedTimeUnix(item);
        if (createdUnix < Number(queryParams.dateFrom)) {
          return false;
        }
      }

      if (queryParams?.dateTo) {
        const createdUnix = getCreatedTimeUnix(item);
        if (createdUnix > Number(queryParams.dateTo)) {
          return false;
        }
      }

      if (queryParams?.rejected && !isRejectedReview(item)) {
        return false;
      }

      if (queryParams?.checked === true && !isApprovedReview(item)) {
        return false;
      }

      if (queryParams?.checked === false && !isPendingReview(item)) {
        return false;
      }

      return true;
    });
  };

  const setStateReviews = React.useCallback(
    (reviewsArr) => {
      dispatch({ type: 'reviews/setReviews', payload: reviewsArr });
    },
    [dispatch]
  );

  const handleReviewApproved = React.useCallback((reviewId) => {
    setGoogleReviews((prev) =>
      prev.map((r) => (r.id === reviewId ? { ...r, checked: true } : r))
    );
    setFacebookReviews((prev) =>
      prev.map((r) => (r.id === reviewId ? { ...r, checked: true } : r))
    );
  }, []);

  // Client selector functions
  const handleChangeClient = React.useCallback(
    async (selectedOption) => {
      if (!selectedOption) return;
      try {
        const response = await axios.get(
          `${API_CLIENTS_NAME}${selectedOption.clientName}`,
          {
            headers: {
              'X-API-KEY': API_KEY,
              Authorization: `bearer ${userInfo.accessToken}`
            }
          }
        );

        if (response?.data) {
          dispatch(setSelectedClient(response.data));
        } else {
          dispatch(setSelectedClient(selectedOption));
        }
      } catch (error) {
        console.error('Error fetching complete client data:', error);
        dispatch(setSelectedClient(selectedOption));
      }
    },
    [userInfo?.accessToken, dispatch]
  );

  const getCustomerList = React.useCallback(() => {
    if (!customerList?.length) {
      dispatch(getClients());
    }

    return customerList.map((customer) => ({
      ...customer,
      label: customer.clientName,
      value: customer.clientName
    }));
  }, [customerList, dispatch]);

  // Cargar Google Average Rating de forma independiente
  useEffect(() => {
    const loadGoogleAverageRating = async () => {
      if (activePlatform === 'facebook') {
        setGoogleAverageRating({ loading: false, data: null });
        return;
      }
      if (!selectedClient?.id) {
        setGoogleAverageRating({ loading: false, data: null });
        return;
      }

      setGoogleAverageRating({ loading: true, data: null });

      try {
        const profileInfo = await getGoogleProfileInfo(selectedClient.id);
        setGoogleAverageRating({
          loading: false,
          data: profileInfo.data?.reviews_info?.averageRating || null
        });
      } catch (error) {
        console.error('Error loading Google average rating:', error);
        setGoogleAverageRating({ loading: false, data: null });
      }
    };

    loadGoogleAverageRating();
  }, [selectedClient?.id, activePlatform]);

  // Reset bulk selection when platform or client changes
  useEffect(() => {
    setSelectedReviews([]);
  }, [activePlatform, selectedClient?.id]);

  const handleApplyFilters = () => {
    const filters = {};

    // Apply date filters
    if (dateFrom) {
      const fromDate = new Date(dateFrom);
      filters.dateFrom = Math.floor(fromDate.getTime() / 1000); // Convert to Unix timestamp
      filters.startDate = filters.dateFrom;
    }

    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999); // End of day
      filters.dateTo = Math.floor(toDate.getTime() / 1000); // Convert to Unix timestamp
      filters.endDate = filters.dateTo;
    }

    // Apply stars filter
    if (starsFilter !== 'all') {
      filters.starRating = starsFilter;
      filters.stars = starsFilter;
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'approved') {
        filters.checked = true;
        filters.status = '1';
      } else if (statusFilter === 'pending') {
        filters.checked = false;
        filters.status = '2';
      } else if (statusFilter === 'rejected') {
        filters.rejected = true;
        filters.status = '3';
      }
    }

    setFilter(filters);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getGoogleReviews = async (queryParams) => {
    if (activePlatform !== 'all') {
      setStateReviews([]);
      setLoading(true);
      dispatch(showLoading());
    }

    axios
      .get(GET_REVIEWS_PER_CLIENT, {
        params: {
          id: selectedClient?.id,
          mode: 'recent',
          ...queryParams
        }
      })
      .then((res) => {
        let data = res.data?.reviews || [];

        data = data.map((item) => {
          item.id = item.reviewId;
          item._platform = 'google';
          item.source = 'google';

          const manager = accountManagers.find(
            (m) => m.id === item.accountOwner
          );
          if (manager) item.assignedTo = manager.full_name;

          return item;
        });

        // Filter by selected client
        if (selectedClient) {
          const gmbLocationName =
            selectedClient.socialnetworks?.googleMyBusinessLocationName;
          const selectedZohoId =
            selectedClient.zohoId || selectedClient.socialnetworks?.zohoId;

          data = data.filter((item) => {
            const matchesLocationName =
              gmbLocationName &&
              item.locationName &&
              item.locationName.trim().toLowerCase() ===
                gmbLocationName.trim().toLowerCase();
            const matchesZohoId =
              selectedZohoId &&
              item.zohoId &&
              String(item.zohoId) === String(selectedZohoId);

            return Boolean(matchesLocationName || matchesZohoId);
          });
        }

        const sortedData = data.sort(
          (a, b) => (b.createdTimeUnix || 0) - (a.createdTimeUnix || 0)
        );

        const filteredData = applyFallbackFilters(sortedData, queryParams);

        setGoogleReviews(filteredData);
        if (activePlatform === 'google') {
          setStateReviews(filteredData);
        }
        if (activePlatform !== 'all') {
          dispatch(hideLoading());
          setLoading(false);
        }
        setInitialLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching Google reviews:', error);
        setGoogleReviews([]);
        if (activePlatform === 'google') {
          setStateReviews([]);
        }
        if (activePlatform !== 'all') {
          dispatch(hideLoading());
          setLoading(false);
        }
        setInitialLoading(false);
      });
  };

  // SEO Audit API functions for Facebook reviews
  const SEO_AUDIT_API = SEO_AUDIT_BASE;

  const getSeoAuditRequestConfig = React.useCallback(
    (baseConfig = {}) => {
      const headers = {
        ...(baseConfig.headers || {})
      };

      if (API_KEY) {
        headers['X-API-KEY'] = API_KEY;
      }

      if (userInfo?.accessToken) {
        headers.Authorization = `bearer ${userInfo.accessToken}`;
      }

      return {
        ...baseConfig,
        headers
      };
    },
    [userInfo?.accessToken]
  );

  const getReviewsFromNewAPI = async (
    platform,
    isSilent = false,
    queryParams = null
  ) => {
    setFacebookLoading(true);
    if (!isSilent && activePlatform !== 'all') {
      setStateReviews([]);
      setLoading(true);
      dispatch(showLoading());
    }

    try {
      const locationId =
        selectedClient?.id || selectedClient?.currentClientId || '';
      const params = { locationId };
      if (platform !== 'all') {
        params.platform = platform;
      }

      const res = await axios.get(
        `${SEO_AUDIT_API}/reviews`,
        getSeoAuditRequestConfig({ params })
      );
      const normalized = res.data?.reviews || [];

      // Map NormalizedReview → existing table row shape
      const mapped = normalized.map((r) => ({
        id: r.reviewId,
        reviewId: r.reviewId,
        rev_comment: r.text,
        starRating: r.rating, // already numeric 1-5
        reviewer: { displayName: r.reviewerName },
        createdTimeUnix: Math.floor(new Date(r.reviewDate).getTime() / 1000),
        _platform: r.platform,
        source: r.platform,
        _isNewAPI: true,
        response: r.response || { status: 'new' },
        msgReply: r.response?.draftText || r.response?.finalText || '',
        checked:
          r.response?.status === 'published' ||
          r.response?.status === 'manual_posting_required',
        rejected: r.response?.status === 'failed',
        locationId: r.locationId,
        locationName: r.platform.charAt(0).toUpperCase() + r.platform.slice(1),
        url: r.url
      }));

      const sorted = mapped.sort(
        (a, b) => b.createdTimeUnix - a.createdTimeUnix
      );

      const filtered = applyFallbackFilters(sorted, queryParams);

      setFacebookReviews(filtered);
      if (activePlatform === 'facebook') {
        setStateReviews(filtered);
      }
    } catch (error) {
      console.error('Error fetching reviews from new API:', error);
      setFacebookReviews([]);
      if (!isSilent && activePlatform === 'facebook') {
        setStateReviews([]);
      }
    } finally {
      setFacebookLoading(false);
      if (!isSilent && activePlatform !== 'all') {
        dispatch(hideLoading());
        setLoading(false);
        setInitialLoading(false);
      } else if (activePlatform === 'all') {
        setInitialLoading(false);
      }
    }
  };

  /**
   * Fetch + ingest Facebook reviews from external platform into DynamoDB,
   * then refresh the table from DynamoDB.
   */
  const fetchAndIngestReviews = async (platform) => {
    if (platform !== 'facebook') return;

    setIngesting(true);
    const locationId =
      selectedClient?.id || selectedClient?.currentClientId || '';

    try {
      const payload = {
        platform: 'facebook',
        locationId,
        notifyEmail: userInfo?.email
      };

      // Load saved settings
      let savedPlatforms = {};
      try {
        const settingsRes = await axios.get(
          `${SEO_AUDIT_API}/reviews/settings`,
          getSeoAuditRequestConfig({ params: { locationId } })
        );
        savedPlatforms = settingsRes.data?.settings?.reviewPlatforms || {};
      } catch (e) {
        console.warn(
          'Could not load saved review settings, using client defaults:',
          e
        );
        savedPlatforms = selectedClient?.reviewPlatforms || {};
      }

      const mergedPlatforms = {
        ...(selectedClient?.reviewPlatforms || {}),
        ...(savedPlatforms || {})
      };

      const fbPageId = mergedPlatforms.facebook?.pageId;
      const fbToken = mergedPlatforms.facebook?.accessToken;

      // Frontend may not have facebook credentials if managed in backend.
      // Backend will resolve them from facebook-dev when missing.
      if (fbPageId) payload.pageId = fbPageId;
      if (fbToken) payload.accessToken = fbToken;

      const resSingle = await axios.post(
        `${SEO_AUDIT_API}/reviews/ingest`,
        payload,
        getSeoAuditRequestConfig()
      );

      const isAsync = resSingle.data && resSingle.data.async;

      if (isAsync) {
        Swal.fire({
          icon: 'info',
          title: 'Syncing Facebook Reviews...',
          html: 'This usually takes 1-2 minutes.<br/><br/>You can keep working while we finish up in the background.',
          allowOutsideClick: false,
          showConfirmButton: true,
          confirmButtonText: 'Hide Loader & continue'
        });

        let attempts = 0;
        const pollInterval = setInterval(() => {
          attempts++;
          getReviewsFromNewAPI(platform, true);

          // Stop polling after ~150 seconds
          if (attempts >= 15) {
            clearInterval(pollInterval);
            if (
              Swal.isVisible() &&
              Swal.getTitle()?.textContent === 'Syncing Facebook Reviews...'
            ) {
              Swal.fire({
                icon: 'success',
                title: 'Sync Finished',
                text: 'Finished waiting for background sync. New reviews have been loaded if found.',
                timer: 3000,
                showConfirmButton: false
              });
            }
          }
        }, 10000);
      } else {
        Swal.fire({
          icon: 'success',
          title: 'Reviews Fetched!',
          text: 'Facebook reviews have been fetched and saved. Loading...',
          timer: 2000,
          showConfirmButton: false
        });

        // Refresh from DynamoDB
        setTimeout(() => {
          getReviewsFromNewAPI(platform);
        }, 1000);
      }
    } catch (error) {
      console.error('Error ingesting Facebook reviews:', error);
      Swal.fire({
        icon: 'error',
        title: 'Fetch Failed',
        text:
          error?.response?.data?.error ||
          error.message ||
          'Failed to fetch reviews from Facebook.'
      });
    } finally {
      setIngesting(false);
    }
  };

  const renderStars = (count) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-5 w-5 ${
            star <= count
              ? 'fill-yellow-400 text-yellow-400'
              : 'fill-muted text-muted'
          }`}
        />
      ))}
    </div>
  );

  const getStatusBadge = (row) => {
    if (!row?.checked) {
      return (
        <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100 text-sm px-3 py-1 rounded-full border-0 shadow-none">
          Pending
        </Badge>
      );
    } else if (row.rejected) {
      return (
        <Badge className="bg-red-100 text-red-700 hover:bg-red-100 text-sm px-3 py-1 rounded-full border-0 shadow-none">
          Rejected
        </Badge>
      );
    } else {
      return (
        <Badge className="bg-green-100 text-green-700 hover:bg-green-100 text-sm px-3 py-1 rounded-full border-0 shadow-none">
          Approved
        </Badge>
      );
    }
  };

  const DateFormat = (cell, row) => {
    if (cell && !row.rejected) {
      if (showGoogleReviews) {
        return (
          <span className="text-sm">
            {new Date(cell * 1000).toLocaleDateString()}
          </span>
        );
      }
      return (
        <span className="text-sm">{new Date(cell).toLocaleDateString()}</span>
      );
    }
    return <span className="text-sm">--</span>;
  };

  useEffect(() => {
    if (!selectedClient?.id && !selectedClient?.currentClientId) return;

    setLoading(true);
    dispatch(showLoading());

    const queryParams = { replied: showReplied, ...filter };

    const promises = [];

    if (activePlatform === 'google' || activePlatform === 'all') {
      promises.push(getGoogleReviews(queryParams));
    }
    if (activePlatform === 'facebook' || activePlatform === 'all') {
      promises.push(getReviewsFromNewAPI('facebook', false, queryParams));
    }

    Promise.allSettled(promises).finally(() => {
      setLoading(false);
      dispatch(hideLoading());
      setInitialLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, showReplied, activePlatform, refresh, selectedClient]);

  const [expandedRows] = useState([]);

  const reviewsSlice = useSelector((state) => state.reviews);

  // Use loading state for skeleton (both initial and filtering)
  const isLoadingData = loading || initialLoading;

  // ─── Per-table card renderer ─────────────────────────────────────────────
  const renderReviewsCard = (title, tableReviews, tableLoading) => {
    const tableData = (tableReviews || []).map((row) => ({
      ...row,
      _expanded: expandedRows.includes(row.id),
      _isEditing:
        reviewsSlice.editable.editable && reviewsSlice.editable.id === row.id
    }));

    return (
      <Card className="rounded-2xl border-2 border-border shadow-sm overflow-hidden mt-6 sm:mt-8">
        <CardHeader className="pb-3 sm:pb-4 px-4 sm:px-6">
          <CardTitle className="text-lg font-medium">
            {title}
            {tableData.length > 0 && ` (${tableData.length})`}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {tableLoading ? (
            <div className="p-6 space-y-4">
              {Array.from({ length: 5 }).map((_, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-4 pb-4 border-b border-border last:border-0">
                  <div className="h-10 w-10 rounded-full bg-gray-200 animate-pulse" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
                    <div className="h-3 w-24 bg-gray-200 rounded animate-pulse" />
                    <div className="h-3 w-full bg-gray-200 rounded animate-pulse" />
                    <div className="h-3 w-3/4 bg-gray-200 rounded animate-pulse" />
                  </div>
                  <div className="flex gap-2">
                    <div className="h-5 w-5 bg-gray-200 rounded animate-pulse" />
                    <div className="h-5 w-5 bg-gray-200 rounded animate-pulse" />
                    <div className="h-5 w-5 bg-gray-200 rounded animate-pulse" />
                    <div className="h-5 w-5 bg-gray-200 rounded animate-pulse" />
                    <div className="h-5 w-5 bg-gray-200 rounded animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : tableData.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No Reviews
            </div>
          ) : (
            <>
              <div className="sm:hidden divide-y divide-border">
                {tableData.map((review) => {
                  const rating =
                    review.source === 'facebook' || review._isNewAPI
                      ? parseFloat(review.starRating)
                      : textRatingToNumber(review.starRating);

                  return (
                    <div key={review.id} className="p-4 space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0">
                          <div
                            className="h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: '#3766A820' }}>
                            <User
                              className="h-5 w-5"
                              style={{ color: '#3766A8' }}
                            />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-sm leading-snug whitespace-normal break-normal">
                              {review.reviewer?.displayName}
                            </p>
                            <p className="text-xs text-muted-foreground whitespace-normal break-normal">
                              {review.locationName}
                            </p>
                            <div className="text-xs text-muted-foreground mt-1">
                              {DateFormat(review.createdTimeUnix, review)}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2 shrink-0">
                          {getStatusBadge(review)}
                          <div className="scale-90 origin-right">
                            {renderStars(rating)}
                          </div>
                        </div>
                      </div>

                      <div className="space-y-1">
                        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          Comment
                        </p>
                        <TruncatedText
                          text={review.rev_comment}
                          maxLength={120}
                        />
                      </div>

                      <div className="space-y-1">
                        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                          Your Response
                        </p>
                        <Answer cell={review.msgReply} row={review} />
                      </div>

                      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1.5">
                          <Clock className="h-3.5 w-3.5" />
                          {DateFormat(review.schedule, review)}
                        </div>
                      </div>

                      <div className="pt-1">
                        <Actions
                          row={review}
                          refresh={() => setRefresh(!refresh)}
                          setLoading={setLoading}
                          onApprove={handleReviewApproved}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="hidden sm:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="text-base font-semibold py-4 px-5 border-r-0">
                        Reviewer
                      </TableHead>
                      <TableHead className="text-base font-semibold py-4 border-r-0">
                        Rating
                      </TableHead>
                      <TableHead className="text-base font-semibold py-4 min-w-[200px] border-r-0">
                        Comment
                      </TableHead>
                      <TableHead className="text-base font-semibold py-4 min-w-[220px] border-r-0">
                        Your Response
                      </TableHead>
                      <TableHead className="text-base font-semibold py-4 border-r-0">
                        Scheduled
                      </TableHead>
                      <TableHead className="text-base font-semibold py-4 border-r-0">
                        Status
                      </TableHead>
                      <TableHead className="text-base font-semibold py-4 px-5">
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tableData.map((review) => {
                      const rating =
                        review.source === 'facebook' || review._isNewAPI
                          ? parseFloat(review.starRating)
                          : textRatingToNumber(review.starRating);

                      return (
                        <TableRow
                          key={review.id}
                          className="align-top hover:bg-muted/30">
                          <TableCell className="py-5 px-5 border-r-0">
                            <div className="flex items-center gap-3">
                              <div
                                className="h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0"
                                style={{ backgroundColor: '#3766A820' }}>
                                <User
                                  className="h-5 w-5"
                                  style={{ color: '#3766A8' }}
                                />
                              </div>
                              <div className="min-w-0">
                                <p className="font-medium text-base leading-tight mb-0 whitespace-normal break-normal">
                                  {review.reviewer?.displayName}
                                </p>
                                {review?.googleLink1 || review?.googleLink2 ? (
                                  <a
                                    href={
                                      review?.googleLink2 || review?.googleLink1
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm hover:underline leading-tight"
                                    style={{ color: '#3766A8' }}>
                                    {review.locationName}
                                  </a>
                                ) : (
                                  <p className="text-sm text-muted-foreground leading-tight">
                                    {review.locationName}
                                  </p>
                                )}
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  {DateFormat(review.createdTimeUnix, review)}
                                </p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="py-5 border-r-0">
                            {renderStars(rating)}
                          </TableCell>
                          <TableCell
                            className="py-5 border-r-0"
                            style={{
                              minWidth: '200px',
                              maxWidth: '300px',
                              verticalAlign: 'top'
                            }}>
                            <TruncatedText
                              text={review.rev_comment}
                              maxLength={60}
                            />
                          </TableCell>
                          <TableCell
                            className="py-5 border-r-0"
                            style={{
                              minWidth: '220px',
                              maxWidth: '320px',
                              verticalAlign: 'top'
                            }}>
                            <Answer cell={review.msgReply} row={review} />
                          </TableCell>
                          <TableCell className="py-5 border-r-0">
                            <div className="flex items-center gap-2 text-base">
                              <Clock className="h-4 w-4 text-muted-foreground" />
                              {DateFormat(review.schedule, review)}
                            </div>
                          </TableCell>
                          <TableCell className="py-5 border-r-0">
                            {getStatusBadge(review)}
                          </TableCell>
                          <TableCell className="py-5 px-5">
                            <Actions
                              row={review}
                              refresh={() => setRefresh(!refresh)}
                              setLoading={setLoading}
                              onApprove={handleReviewApproved}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    );
  };
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-muted/30 overflow-x-hidden">
      {/* Main Content */}
      <div className="flex-1 min-w-0">
        {/* Content Area */}
        <main className="p-3 sm:p-6 lg:p-10">
          {/* Welcome Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4 mb-6 sm:mb-8">
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground break-words">
                Customer Reviews
                {selectedClient ? `, ${selectedClient.clientName}` : ''}
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground mt-1">
                See what your customers are saying and respond to them
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
              {isSuperAdmin && !isWebviewMode && (
                <div className="relative w-full sm:w-72">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground z-10" />
                  <ReactSelect
                    placeholder="Search"
                    onChange={(client) => handleChangeClient(client)}
                    options={getCustomerList()}
                    hideSelectedOptions={true}
                    controlShouldRenderValue={false}
                    components={{
                      DropdownIndicator: () => null,
                      IndicatorSeparator: () => null
                    }}
                    openMenuOnClick={false}
                    openMenuOnFocus={false}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      control: (provided, state) => ({
                        ...provided,
                        borderRadius: '0.75rem',
                        borderWidth: '1px',
                        borderColor: state.isFocused
                          ? 'hsl(var(--primary))'
                          : 'hsl(var(--border))',
                        boxShadow: state.isFocused
                          ? '0 0 0 1px hsl(var(--primary))'
                          : 'none',
                        backgroundColor: '#ffffff',
                        paddingTop: '10px',
                        paddingBottom: '9px',
                        paddingLeft: '2.5rem',
                        fontSize: '1rem',
                        '&:hover': {
                          borderColor: 'hsl(var(--primary))'
                        }
                      }),
                      placeholder: (provided) => ({
                        ...provided,
                        color: 'hsl(var(--muted-foreground))',
                        fontSize: '1rem'
                      }),
                      input: (provided) => ({
                        ...provided,
                        fontSize: '1rem',
                        color: 'hsl(var(--foreground))'
                      }),
                      menu: (provided) => ({
                        ...provided,
                        borderRadius: '0.75rem',
                        overflow: 'hidden',
                        boxShadow:
                          '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)'
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isFocused ? '#f1f5f9' : 'white',
                        color: state.isFocused ? '#1e293b' : '#64748b',
                        cursor: 'pointer',
                        padding: '12px 16px',
                        fontSize: '1rem'
                      })
                    }}
                  />
                </div>
              )}
              <Button
                variant="outline"
                size="lg"
                onClick={() => history.push('/request-reviews')}
                style={{ paddingLeft: '2rem', paddingRight: '2rem' }}
                className="h-12 sm:h-14 w-full sm:w-auto text-sm sm:text-lg font-semibold rounded-xl shadow-sm hover:shadow-md transition-all gap-2 sm:gap-3 flex items-center justify-center">
                <MessageSquarePlus className="h-5 w-5" />
                <span>Request</span>
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => history.push('/reviews-settings')}
                style={{ paddingLeft: '2rem', paddingRight: '2rem' }}
                className="h-12 sm:h-14 w-full sm:w-auto text-sm sm:text-lg font-semibold rounded-xl shadow-sm hover:shadow-md transition-all gap-2 sm:gap-3 flex items-center justify-center">
                <Settings className="h-5 w-5" />
                <span>Settings</span>
              </Button>
              {activePlatform === 'facebook' && (
                <Button
                  size="lg"
                  onClick={() => fetchAndIngestReviews('facebook')}
                  disabled={ingesting}
                  style={{ paddingLeft: '2rem', paddingRight: '2rem' }}
                  className="h-12 sm:h-14 w-full sm:w-auto text-sm sm:text-lg font-semibold rounded-xl shadow-sm hover:shadow-md transition-all gap-2 sm:gap-3 flex items-center justify-center bg-primary hover:bg-primary/90 text-primary-foreground">
                  {ingesting ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <RefreshCw className="h-5 w-5" />
                  )}
                  {ingesting ? (
                    <span>Fetching...</span>
                  ) : (
                    <span>Fetch FB Reviews</span>
                  )}
                </Button>
              )}
            </div>
          </div>

          {/* Unified Platform Stats Card */}
          <Card className="rounded-xl sm:rounded-2xl border-2 border-border shadow-sm overflow-hidden mb-4 sm:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-stretch divide-y sm:divide-y-0 sm:divide-x divide-border">
              {/* Google Stats */}
              {activePlatform !== 'facebook' && (
                <div className="flex-1 flex items-center">
                  <div className="flex items-center gap-2 px-3 sm:px-4 shrink-0">
                    <span className="h-2 w-2 rounded-full bg-foreground/60" />
                    <span className="text-xs sm:text-sm font-semibold text-foreground">
                      Google
                    </span>
                  </div>
                  <div className="grid grid-cols-3 flex-1 divide-x divide-border">
                    <div className="flex items-center justify-center gap-2 py-2.5 sm:py-3 px-2">
                      <Check className="h-4 w-4 text-green-600 shrink-0" />
                      <div className="leading-tight">
                        {isLoadingData ? (
                          <>
                            <div className="h-5 w-6 bg-gray-200 rounded animate-pulse mb-0.5" />
                            <div className="h-3 w-14 bg-gray-200 rounded animate-pulse" />
                          </>
                        ) : googleReviews.length === 0 ? (
                          <>
                            <p className="text-base sm:text-lg font-bold text-muted-foreground leading-none">
                              —
                            </p>
                            <p className="text-[10px] sm:text-xs text-muted-foreground">
                              No Reviews
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="text-base sm:text-lg font-bold text-foreground leading-none">
                              {googleReviews?.filter(
                                (r) => r.checked && !r.rejected
                              ).length || 0}
                            </p>
                            <p className="text-[10px] sm:text-xs text-muted-foreground">
                              Approved
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-center gap-2 py-2.5 sm:py-3 px-2">
                      <Clock className="h-4 w-4 text-yellow-600 shrink-0" />
                      <div className="leading-tight">
                        {isLoadingData ? (
                          <>
                            <div className="h-5 w-6 bg-gray-200 rounded animate-pulse mb-0.5" />
                            <div className="h-3 w-14 bg-gray-200 rounded animate-pulse" />
                          </>
                        ) : googleReviews.length === 0 ? (
                          <>
                            <p className="text-base sm:text-lg font-bold text-muted-foreground leading-none">
                              —
                            </p>
                            <p className="text-[10px] sm:text-xs text-muted-foreground">
                              No Reviews
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="text-base sm:text-lg font-bold text-foreground leading-none">
                              {googleReviews?.filter(
                                (r) => !r.checked && !r.rejected
                              ).length || 0}
                            </p>
                            <p className="text-[10px] sm:text-xs text-muted-foreground">
                              Pending
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-center gap-2 py-2.5 sm:py-3 px-2">
                      <Star className="h-4 w-4 text-primary fill-primary shrink-0" />
                      <div className="leading-tight">
                        {googleAverageRating.loading ? (
                          <>
                            <div className="h-5 w-6 bg-gray-200 rounded animate-pulse mb-0.5" />
                            <div className="h-3 w-14 bg-gray-200 rounded animate-pulse" />
                          </>
                        ) : googleReviews.length === 0 ? (
                          <>
                            <p className="text-base sm:text-lg font-bold text-muted-foreground leading-none">
                              —
                            </p>
                            <p className="text-[10px] sm:text-xs text-muted-foreground">
                              No Reviews
                            </p>
                          </>
                        ) : googleAverageRating.data !== null ? (
                          <>
                            <p className="text-base sm:text-lg font-bold text-foreground leading-none">
                              {googleAverageRating.data.toFixed(1)}
                            </p>
                            <p className="text-[10px] sm:text-xs text-muted-foreground">
                              Avg. Rating
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="text-base sm:text-lg font-bold text-foreground leading-none">
                              —
                            </p>
                            <p className="text-[10px] sm:text-xs text-muted-foreground">
                              Avg. Rating
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Facebook Stats */}
              {activePlatform !== 'google' && (
                <div className="flex-1 flex items-center">
                  <div className="flex items-center gap-2 px-3 sm:px-4 shrink-0">
                    <span className="h-2 w-2 rounded-full bg-foreground/60" />
                    <span className="text-xs sm:text-sm font-semibold text-foreground">
                      Facebook
                    </span>
                  </div>
                  <div className="grid grid-cols-3 flex-1 divide-x divide-border">
                    <div className="flex items-center justify-center gap-2 py-2.5 sm:py-3 px-2">
                      <Check className="h-4 w-4 text-green-600 shrink-0" />
                      <div className="leading-tight">
                        {facebookLoading || isLoadingData ? (
                          <>
                            <div className="h-5 w-6 bg-gray-200 rounded animate-pulse mb-0.5" />
                            <div className="h-3 w-14 bg-gray-200 rounded animate-pulse" />
                          </>
                        ) : facebookReviews.length === 0 ? (
                          <>
                            <p className="text-base sm:text-lg font-bold text-muted-foreground leading-none">
                              —
                            </p>
                            <p className="text-[10px] sm:text-xs text-muted-foreground">
                              No Reviews
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="text-base sm:text-lg font-bold text-foreground leading-none">
                              {facebookReviews?.filter(
                                (r) => r.checked && !r.rejected
                              ).length || 0}
                            </p>
                            <p className="text-[10px] sm:text-xs text-muted-foreground">
                              Approved
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-center gap-2 py-2.5 sm:py-3 px-2">
                      <Clock className="h-4 w-4 text-yellow-600 shrink-0" />
                      <div className="leading-tight">
                        {facebookLoading || isLoadingData ? (
                          <>
                            <div className="h-5 w-6 bg-gray-200 rounded animate-pulse mb-0.5" />
                            <div className="h-3 w-14 bg-gray-200 rounded animate-pulse" />
                          </>
                        ) : facebookReviews.length === 0 ? (
                          <>
                            <p className="text-base sm:text-lg font-bold text-muted-foreground leading-none">
                              —
                            </p>
                            <p className="text-[10px] sm:text-xs text-muted-foreground">
                              No Reviews
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="text-base sm:text-lg font-bold text-foreground leading-none">
                              {facebookReviews?.filter(
                                (r) => !r.checked && !r.rejected
                              ).length || 0}
                            </p>
                            <p className="text-[10px] sm:text-xs text-muted-foreground">
                              Pending
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-center gap-2 py-2.5 sm:py-3 px-2">
                      <Star className="h-4 w-4 text-primary fill-primary shrink-0" />
                      <div className="leading-tight">
                        {facebookLoading || isLoadingData ? (
                          <>
                            <div className="h-5 w-6 bg-gray-200 rounded animate-pulse mb-0.5" />
                            <div className="h-3 w-14 bg-gray-200 rounded animate-pulse" />
                          </>
                        ) : facebookReviews.length === 0 ? (
                          <>
                            <p className="text-base sm:text-lg font-bold text-muted-foreground leading-none">
                              —
                            </p>
                            <p className="text-[10px] sm:text-xs text-muted-foreground">
                              No Reviews
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="text-base sm:text-lg font-bold text-foreground leading-none">
                              {(() => {
                                const sum = facebookReviews.reduce(
                                  (acc, r) =>
                                    acc + (parseFloat(r.starRating) || 0),
                                  0
                                );
                                return (sum / facebookReviews.length).toFixed(
                                  1
                                );
                              })()}
                            </p>
                            <p className="text-[10px] sm:text-xs text-muted-foreground">
                              Avg. Rating
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Compact Filters */}
          <div className="space-y-3 lg:space-y-0 lg:flex lg:flex-nowrap lg:items-center lg:gap-3 mb-6 mt-6 sm:mt-8">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Filter className="h-4 w-4" />
              <span>Filters:</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] lg:grid-cols-[145px_auto_145px] gap-2 sm:gap-3 items-center lg:w-auto">
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-10 sm:h-9 w-full rounded-lg text-sm bg-white border-gray-300"
              />
              <span className="text-muted-foreground text-sm text-center">
                to
              </span>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-10 sm:h-9 w-full rounded-lg text-sm bg-white border-gray-300"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[135px_145px_180px] gap-2 sm:gap-3 lg:w-auto">
              <Select value={starsFilter} onValueChange={setStarsFilter}>
                <SelectTrigger className="h-10 sm:h-9 w-full rounded-lg text-sm bg-white border-gray-300">
                  <SelectValue placeholder="Rating" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Ratings</SelectItem>
                  <SelectItem value="5">5 Stars</SelectItem>
                  <SelectItem value="4">4 Stars</SelectItem>
                  <SelectItem value="3">3 Stars</SelectItem>
                  <SelectItem value="2">2 Stars</SelectItem>
                  <SelectItem value="1">1 Star</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-10 sm:h-9 w-full rounded-lg text-sm bg-white border-gray-300">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              <Select value={activePlatform} onValueChange={setActivePlatform}>
                <SelectTrigger className="h-10 sm:h-9 w-full rounded-lg text-sm bg-white border-gray-300">
                  <SelectValue placeholder="Platform" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Platforms</SelectItem>
                  <SelectItem value="google">Google Reviews</SelectItem>
                  <SelectItem value="facebook">Facebook Reviews</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 sm:justify-start lg:w-auto">
              <Button
                size="sm"
                onClick={handleApplyFilters}
                className="h-10 sm:h-9 w-full sm:w-auto px-4 text-sm gap-2 text-white bg-primary hover:bg-primary/90 rounded-lg shadow-sm">
                <Filter className="h-3.5 w-3.5" />
                Filter
              </Button>
              <Button
                variant={showReplied ? 'default' : 'outline'}
                size="sm"
                onClick={() => setShowReplied(!showReplied)}
                className="h-10 sm:h-9 w-full sm:w-auto px-4 text-sm gap-2 rounded-lg shadow-sm">
                {showReplied ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  <X className="h-3.5 w-3.5" />
                )}
                {showReplied ? 'Hide Replied' : 'Show Replied'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFilter(null);
                  setDateFrom(formatDateToInput(getDaysAgoUnix()));
                  setDateTo(formatDateToInput(getCurrentUnix()));
                  setStarsFilter('all');
                  setStatusFilter('all');
                  setActivePlatform('all');
                  setShowReplied(false);
                }}
                className="h-10 sm:h-9 w-full sm:w-auto px-3 text-sm gap-1.5 text-foreground bg-muted/30 hover:bg-gray-200 border-0">
                <RotateCcw className="h-3.5 w-3.5" />
                Clear
              </Button>
            </div>
          </div>

          {/* Reviews Tables */}
          {activePlatform === 'all' ? (
            <>
              {renderReviewsCard(
                'Google Reviews',
                googleReviews,
                isLoadingData
              )}
              {renderReviewsCard(
                'Facebook Reviews',
                facebookReviews,
                facebookLoading || isLoadingData
              )}
            </>
          ) : (
            renderReviewsCard(
              activePlatform === 'google'
                ? 'Google Reviews'
                : 'Facebook Reviews',
              activePlatform === 'google' ? googleReviews : facebookReviews,
              isLoadingData
            )
          )}
        </main>
      </div>

      {/* Mantengo esto porque en Answer se usa animation: 'spin ...' */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(310deg); }
          to { transform: rotate(670deg); }
        }
        
        /* Force button border colors and width */
        button.h-9.rounded-lg.text-sm.gap-2.justify-start:has(.lucide-check) {
          border-color: #86efac !important;
          border-width: 0.5px !important;
        }
        
        button.h-9.rounded-lg.text-sm.gap-2.justify-start:has(.lucide-trash-2) {
          border-color: #fca5a5 !important;
          border-width: 0.5px !important;
        }
        
        button.h-9.rounded-lg.text-sm.gap-2.justify-start:has(.lucide-pencil) {
          border-color: #cbd5e1 !important;
          border-width: 0.5px !important;
        }
      `}</style>
    </div>
  );
};

export default Reviews;
